// client/src/coms/organisation/channelLifecycleUi.js
// Channel lifecycle UI: the read-only bar shown in archived channels (and the auto-archive warning), and the
// dialogs behind the channel context menu (archive with a reason, auto-archive, conversions, categories,
// bulk archive/restore). The server enforces Manage channels for every action.
import React, { useCallback, useEffect, useState } from 'react';
import { apiGetJson, apiPostJson, apiPatchJson, apiDelete, apiPut } from './orgApi';
import { Dialog, formatDate, notify, parseTime, Spinner, ErrorNote, openArea, usePrompt } from './orgShared';
import { moveToCategory } from './channelLayout';

// Live state of a text channel (archived / auto-archive). Refreshes on the chat stream's channel_state event.
export function useChannelState(channelDbId, enabled = true) {
  const [state, setState] = useState(null);
  const load = useCallback(() => {
    if (!enabled || !channelDbId) {
      setState(null);
      return;
    }
    apiGetJson(`/text-channels/${encodeURIComponent(String(channelDbId))}/state`).then(setState, () => setState(null));
  }, [channelDbId, enabled]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const on = (e) => {
      const d = e.detail || {};
      if (String(d.channelId) === String(channelDbId)) setState((s) => ({ ...(s || {}), ...d.state }));
    };
    window.addEventListener('vox-channel-state', on);
    return () => window.removeEventListener('vox-channel-state', on);
  }, [channelDbId]);
  return { state, reload: load };
}

export function notifyChannelsChanged() {
  try {
    window.dispatchEvent(new CustomEvent('vox-channels-changed'));
  } catch {}
}

export function ArchivedChannelBar({ state, serverKey, channelDbId, onRestored }) {
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  if (state.archived) {
    return (
      <div className="vxo-archived-bar vx-pz" role="status" data-testid="archived-bar">
        <span aria-hidden="true">🗄️</span>
        <span>
          <strong>This channel is archived</strong> and read-only{state.archived_at ? ` since ${formatDate(state.archived_at)}` : ''}.{state.archive_reason ? ` Reason: ${state.archive_reason}` : ''} History, files and permissions are kept.
        </span>
        {state.can_manage ? (
          <button
            type="button"
            className="vx-btn small"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await apiPostJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channelDbId}/restore`, {});
                notify('Channel restored', { tone: 'success' });
                notifyChannelsChanged();
                onRestored && onRestored();
              } catch (e) {
                notify(e.message, { tone: 'error' });
              } finally {
                setBusy(false);
              }
            }}
          >
            Restore channel
          </button>
        ) : null}
      </div>
    );
  }
  const at = parseTime(state.auto_archive_at);
  if (at && at.getTime() - Date.now() < 24 * 3600 * 1000) {
    return (
      <div className="vxo-autoarchive-note" role="status">
        This channel archives automatically {at.getTime() < Date.now() ? 'soon' : formatDate(state.auto_archive_at)} unless someone posts (after {state.auto_archive_days} days without messages).
      </div>
    );
  }
  return null;
}

// ---------------- dialogs ----------------
export function ArchiveDialog({ serverKey, channel, onClose }) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);
  const [needWelcome, setNeedWelcome] = useState(false);
  async function go() {
    try {
      await apiPostJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channel.db_id || channel.id}/archive`, { reason, confirm_welcome: needWelcome });
      notify(`#${channel.name} archived`, { tone: 'success' });
      notifyChannelsChanged();
      onClose(true);
    } catch (e) {
      if (e.data && e.data.code === 'confirm_welcome') {
        setNeedWelcome(true);
        setErr(e);
        return;
      }
      setErr(e);
    }
  }
  return (
    <Dialog
      title={`Archive #${channel.name}?`}
      onClose={() => onClose(false)}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={() => onClose(false)}>
            Cancel
          </button>
          <button type="button" className="vx-btn primary" onClick={go} data-testid="archive-confirm">
            {needWelcome ? 'Archive anyway' : 'Archive'}
          </button>
        </>
      }
    >
      <p>Archived channels keep every message, file and permission, but nobody can post in them. They move to the Archived section and can be restored at any time.</p>
      <label className="vxo-field">
        <span>Reason (optional, shown in the channel)</span>
        <input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="Season finished" />
      </label>
      <ErrorNote error={err} />
    </Dialog>
  );
}

export function AutoArchiveDialog({ serverKey, channel, onClose }) {
  const [state, setState] = useState(null);
  const [days, setDays] = useState('');
  useEffect(() => {
    apiGetJson(`/text-channels/${channel.db_id || channel.id}/state`).then((s) => {
      setState(s);
      setDays(s.auto_archive_days ? String(s.auto_archive_days) : '');
    }, () => setState({}));
  }, [channel]);
  return (
    <Dialog
      title={`Auto-archive #${channel.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            onClick={async () => {
              try {
                await apiPatchJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channel.db_id || channel.id}/auto-archive`, { days: days ? Number(days) : null });
                notify(days ? `#${channel.name} archives after ${days} days without messages` : 'Auto-archive switched off', { tone: 'success' });
                onClose();
              } catch (e) {
                notify(e.message, { tone: 'error' });
              }
            }}
          >
            Save
          </button>
        </>
      }
    >
      {!state ? <Spinner /> : null}
      <p>Archive this channel automatically when nobody has posted for a while. Off by default. It is skipped while people have the channel open, while a “What shall we play” vote is running, and for the welcome channel.</p>
      <label className="vxo-field">
        <span>Archive after</span>
        <select value={days} onChange={(e) => setDays(e.target.value)} data-testid="auto-archive-days">
          <option value="">Never (off)</option>
          {[1, 3, 7, 14, 30, 90].map((d) => (
            <option key={d} value={d}>
              {d} day{d === 1 ? '' : 's'} without messages
            </option>
          ))}
        </select>
      </label>
      {state && state.last_activity_at ? <p className="vxo-muted">Last message: {formatDate(state.last_activity_at)}</p> : null}
    </Dialog>
  );
}

export function ConvertDialog({ serverKey, channel, onClose }) {
  const [info, setInfo] = useState(null);
  const [title, setTitle] = useState(channel.name);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiGetJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channel.db_id || channel.id}/conversions`).then(setInfo, setErr);
  }, [serverKey, channel]);
  const res = info && info.supported.find((s) => s.to === 'resource');
  return (
    <Dialog
      title={`Convert #${channel.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            disabled={!info}
            onClick={async () => {
              try {
                const r = await apiPostJson(`/servers/${encodeURIComponent(serverKey)}/channels/${channel.db_id || channel.id}/convert`, { to: 'resource', title });
                notify('Converted to an archived resource', { tone: 'success', action: { label: 'Open', onClick: () => openArea('library', { tab: 'resources', resource_id: r.resource_id }) } });
                notifyChannelsChanged();
                onClose();
              } catch (e) {
                setErr(e);
              }
            }}
            data-testid="convert-confirm"
          >
            Convert to archived resource
          </button>
        </>
      }
    >
      <ErrorNote error={err} />
      {!info && !err ? <Spinner /> : null}
      {info ? (
        <>
          <h3 className="vxo-h3">Archived resource</h3>
          <p>The channel is archived (history, authors, timestamps and files kept) and listed under Resources so the discussion stays easy to find.</p>
          <p className="vxo-hint">Who can see it: {res && res.audience}</p>
          <label className="vxo-field">
            <span>Resource title</span>
            <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <h3 className="vxo-h3">Not possible</h3>
          <ul className="vxo-plain">
            {info.unsupported.map((u) => (
              <li key={u.to}>
                <strong>{u.to === 'thread' ? 'Thread → channel' : u.to === 'voice' ? 'Text → voice channel' : 'Voice → text channel'}:</strong> {u.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Dialog>
  );
}

// Moves a text or voice channel to the end of a category (or out of categories). With the shared layout
// (layout + onSave from the sidebar) the move goes through the same save path as drag-and-drop.
export function MoveToCategoryDialog({ serverKey, channel, categories, layout = null, onSave = null, onClose }) {
  const [cat, setCat] = useState(channel.category_id ? String(channel.category_id) : '');
  return (
    <Dialog
      title={`Move ${channel.kind === 'voice' ? '🔊' : '#'}${channel.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            onClick={async () => {
              try {
                const kind = channel.kind === 'voice' ? 'voice' : 'text';
                const id = Number(channel.db_id || channel.id);
                if (layout && onSave) {
                  const next = moveToCategory(layout, `${kind}:${id}`, cat ? `category:${cat}` : '');
                  if (next) await onSave(next);
                } else {
                  await apiPut(`/servers/${encodeURIComponent(serverKey)}/channel-layout`, { channels: [{ kind, id, category_id: cat ? Number(cat) : null, position: 1000000 }] });
                  notifyChannelsChanged();
                }
                onClose();
              } catch (e) {
                notify(e.message, { tone: 'error' });
              }
            }}
          >
            Move
          </button>
        </>
      }
    >
      <label className="vxo-field">
        <span>Category</span>
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">No category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
    </Dialog>
  );
}

// Categories and bulk archive/restore in one manager.
export function ChannelOrganiserDialog({ serverKey, channels, onClose, onOpenSettings = null }) {
  const [cats, setCats] = useState(null);
  const [archived, setArchived] = useState(null);
  const [name, setName] = useState('');
  const [sel, setSel] = useState([]);
  const [selArch, setSelArch] = useState([]);
  const [err, setErr] = useState(null);
  const [prompt, promptEl] = usePrompt();
  const base = `/servers/${encodeURIComponent(serverKey)}`;
  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([apiGetJson(`${base}/categories`), apiGetJson(`${base}/archived-channels`)]);
      setCats(c.categories);
      setArchived(a.channels);
    } catch (e) {
      setErr(e);
    }
  }, [base]);
  useEffect(() => {
    load();
  }, [load]);
  const active = channels.filter((c) => c.kind === 'text' && !c.archived_at);
  async function bulk(action, ids) {
    try {
      const r = await apiPostJson(`${base}/channels/bulk`, { action, channel_ids: ids });
      const skipped = r.results.filter((x) => x.skipped).length;
      notify(`${action === 'archive' ? 'Archived' : 'Restored'} ${r.results.filter((x) => x.archived || x.restored).length} channel(s)${skipped ? `; ${skipped} skipped (welcome channel)` : ''}`, { tone: 'success' });
      setSel([]);
      setSelArch([]);
      notifyChannelsChanged();
      load();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function moveCat(c, delta) {
    const list = [...cats];
    const i = list.findIndex((x) => x.id === c.id);
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    await apiPut(`${base}/channel-layout`, { categories: list.map((x, k) => ({ id: x.id, position: k })) }).catch(() => {});
    notifyChannelsChanged();
    load();
  }
  return (
    <Dialog title="Organise channels" onClose={onClose} wide>
      <ErrorNote error={err} />
      {!cats ? <Spinner /> : null}
      {cats ? (
        <div className="vxo-organiser">
          <section>
            <h3 className="vxo-h3">Categories</h3>
            <ul className="vxo-plain vxo-cat-list">
              {cats.map((c) => (
                <li key={c.id}>
                  <span>{c.name}</span>
                  <span className="vxo-nav-tools">
                    <button type="button" className="vx-icon-btn" aria-label={`Move ${c.name} up`} onClick={() => moveCat(c, -1)}>
                      ↑
                    </button>
                    <button type="button" className="vx-icon-btn" aria-label={`Move ${c.name} down`} onClick={() => moveCat(c, 1)}>
                      ↓
                    </button>
                    <button
                      type="button"
                      className="vx-icon-btn"
                      aria-label={`Rename ${c.name}`}
                      onClick={async () => {
                        const n = await prompt({ title: 'Rename category', label: 'Category name', value: c.name, maxLength: 64 });
                        if (!n) return;
                        await apiPatchJson(`${base}/categories/${c.id}`, { name: n }).catch((e) => notify(e.message, { tone: 'error' }));
                        notifyChannelsChanged();
                        load();
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="vx-icon-btn"
                      aria-label={`Delete ${c.name}`}
                      onClick={async () => {
                        await apiDelete(`${base}/categories/${c.id}`).catch((e) => notify(e.message, { tone: 'error' }));
                        notifyChannelsChanged();
                        load();
                      }}
                    >
                      🗑
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <form
              className="vxo-inline-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!name.trim()) return;
                await apiPostJson(`${base}/categories`, { name: name.trim() }).catch((er) => notify(er.message, { tone: 'error' }));
                setName('');
                notifyChannelsChanged();
                load();
              }}
            >
              <input className="vxo-input" value={name} maxLength={64} placeholder="New category" aria-label="New category name" onChange={(e) => setName(e.target.value)} />
              <button type="submit" className="vx-btn">
                Add
              </button>
            </form>
            <p className="vxo-hint">Drag channels, categories and dividers in the channel list (text and voice channels can be mixed in any order), use Alt+↑/↓, or “Move up / Move down / Move to category…” in a channel’s menu.</p>
            {onOpenSettings ? (
              <button type="button" className="vx-btn small" onClick={onOpenSettings} data-testid="open-organisation-settings">
                Open Channels &amp; Organisation
              </button>
            ) : null}
          </section>
          <section>
            <h3 className="vxo-h3">Archive channels</h3>
            <div className="vxo-checks">
              {active.map((c) => (
                <label key={c.id} className="vxo-check">
                  <input type="checkbox" checked={sel.includes(Number(c.db_id || c.id))} onChange={(e) => setSel(e.target.checked ? [...sel, Number(c.db_id || c.id)] : sel.filter((x) => x !== Number(c.db_id || c.id)))} />
                  <span>#{c.name}</span>
                </label>
              ))}
            </div>
            <button type="button" className="vx-btn" disabled={!sel.length} onClick={() => bulk('archive', sel)}>
              Archive {sel.length || ''} selected
            </button>
          </section>
          <section>
            <h3 className="vxo-h3">Archived channels</h3>
            {archived && !archived.length ? <p className="vxo-muted">None.</p> : null}
            <div className="vxo-checks">
              {(archived || []).map((c) => (
                <label key={c.id} className="vxo-check">
                  <input type="checkbox" checked={selArch.includes(c.id)} onChange={(e) => setSelArch(e.target.checked ? [...selArch, c.id] : selArch.filter((x) => x !== c.id))} />
                  <span>
                    #{c.name} <span className="vxo-muted">· {c.message_count} messages · archived {formatDate(c.archived_at, { time: false })}{c.auto ? ' automatically' : ''}{c.archive_reason ? ` · ${c.archive_reason}` : ''}</span>
                  </span>
                </label>
              ))}
            </div>
            <button type="button" className="vx-btn" disabled={!selArch.length} onClick={() => bulk('restore', selArch)}>
              Restore {selArch.length || ''} selected
            </button>
          </section>
        </div>
      ) : null}
      {promptEl}
    </Dialog>
  );
}
